import { Injectable, NgZone } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription
} from 'rxjs';
import {
  distinctUntilChanged
} from 'rxjs/operators';
import {
  Client,
  IMessage,
  StompSubscription
} from '@stomp/stompjs';
// import * as SockJSClient from 'sockjs-client';

import { environment } from '../../environments/environment';
import { AuthService } from '../auth/AuthService';
import {
  NotificationResponse
} from '../Model/notification-response';

export interface NotificationPageResponse {
  content: NotificationResponse[];
  number: number;
  size: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationWebsocketService {

  private stompClient: Client | null = null;
  private topicSubscription: StompSubscription | null = null;
  private clientIdSubscription: Subscription | null = null;
  private loginSubscription: Subscription | null = null;
  private currentUserId: string | null = null;

  private initialized = false;
  private currentClientId: number | null = null;
  private subscribedDestination: string | null = null;

  private notificationsSubject =
    new BehaviorSubject<NotificationResponse[]>([]);

  private unreadCountSubject =
    new BehaviorSubject<number>(0);

  private connectedSubject =
    new BehaviorSubject<boolean>(false);

  private errorSubject =
    new Subject<string>();

  private liveNotificationSubject =
  new Subject<NotificationResponse>();

  liveNotification$: Observable<NotificationResponse> =
    this.liveNotificationSubject.asObservable();

  notifications$: Observable<NotificationResponse[]> =
    this.notificationsSubject.asObservable();

  unreadCount$: Observable<number> =
    this.unreadCountSubject.asObservable();

  connected$: Observable<boolean> =
    this.connectedSubject.asObservable();

  errors$: Observable<string> =
    this.errorSubject.asObservable()

  constructor(
     private authService: AuthService,
     private ngZone: NgZone
  ) { }

  getMyNotifications(
    page: number = 0,
    size: number = 20
  ): Observable<NotificationPageResponse> {
    return this.authService.getP(
      `${environment.patientService}notifications/me` +
      `?page=${page}&size=${size}`
    );
  }

  markAllAsReadOnServer(): Observable<any> {
  return this.authService.put(
    `${environment.patientService}notifications/read-all`,
    {}
  );
  }

  setNotificationHistory(
  history: NotificationResponse[]
): void {
  const normalizedHistory =
    (history || []).map(
      (notification: NotificationResponse) => ({
        ...notification,
        read:
          typeof notification.read === 'boolean'
            ? notification.read
            : false,
        readAt: notification.readAt || null
      })
    );

  /*
   * Preserve WebSocket notifications that may have arrived
   * while the history API was loading.
   */
  const combined = [
    ...this.notificationsSubject.value,
    ...normalizedHistory
  ];

  const notificationMap =
    new Map<number, NotificationResponse>();

  combined.forEach(
    (notification: NotificationResponse) => {
      if (!notificationMap.has(notification.id)) {
        notificationMap.set(
          notification.id,
          notification
        );
      }
    }
  );

  const notifications =
    Array.from(notificationMap.values())
      .sort(
        (
          first: NotificationResponse,
          second: NotificationResponse
        ) =>
          new Date(second.eventTime).getTime() -
          new Date(first.eventTime).getTime()
      )
      .slice(0, 100);

  this.notificationsSubject.next(notifications);
  this.updateUnreadCount(notifications);
  }

  // initialize(): void {
  // console.log('[Notifications] initialize()');

  // if (this.initialized) {
  //   console.log('[Notifications] already initialized');
  //   return;
  // }

  // this.initialized = true;

  // this.clientIdSubscription =
  //   this.authService.clientId$
  //     .pipe(distinctUntilChanged())
  //     .subscribe((clientId: number | null) => {
  //       console.log(
  //         '[Notifications] clientId emitted:',
  //         clientId
  //       );

  //       this.handleClientChange(clientId);
  //     });

  // this.loginSubscription =
  //   this.authService.loginChanged
  //     .pipe(distinctUntilChanged())
  //     .subscribe((loggedIn: boolean) => {
  //       console.log(
  //         '[Notifications] login state:',
  //         loggedIn
  //       );

  //       if (!loggedIn) {
  //         console.log(
  //           '[Notifications] logout detected, disconnecting'
  //         );

  //         this.disconnect();
  //       }
  //     });
  // }

  initialize(): void {

    console.log(
      '[Notifications] initialize()'
    );

    if (this.initialized) {
      return;
    }

    this.initialized = true;

    /*
    * Keep clientId only for notification/business context.
    * It no longer controls WebSocket connectivity.
    */
    this.clientIdSubscription =
      this.authService.clientId$
        .pipe(
          distinctUntilChanged()
        )
        .subscribe(
          (clientId: number | null) => {

            this.currentClientId =
              clientId
                ? Number(clientId)
                : null;
          }
        );

    /*
    * Handle future login/logout changes.
    */
    this.loginSubscription =
      this.authService.loginChanged
        .pipe(
          distinctUntilChanged()
        )
        .subscribe(
          (loggedIn: boolean) => {

            console.log(
              '[Notifications] login state changed:',
              loggedIn
            );

            if (loggedIn) {

              this.connect();

            } else {

              this.disconnect();
            }
          }
        );

    /*
    * IMPORTANT:
    * initialize() may execute AFTER loginChanged already
    * emitted true.
    *
    * Check the CURRENT auth state too.
    */
    this.connectForCurrentSession();
  }
  private async connectForCurrentSession(): Promise<void> {

    try {

      const loggedIn =
        await this.authService.isLoggedIn();

      console.log(
        '[Notifications] current login state:',
        loggedIn
      );

      if (loggedIn) {
        this.connect();
      }

    } catch (error) {

      console.error(
        '[Notifications] unable to determine login state:',
        error
      );
    }
  }
//   private handleClientChange(
//   clientId: number | null
// ): void {
//   // console.log(
//   //   '[Notifications] handleClientChange:',
//   //   clientId
//   // );

//   const normalizedClientId =
//     clientId ? Number(clientId) : null;

//   if (!normalizedClientId) {
//     // console.warn(
//     //   '[Notifications] no valid clientId'
//     // );

//     this.currentClientId = null;
//     this.unsubscribeFromCurrentTopic();
//     return;
//   }

//   const clientChanged =
//     this.currentClientId !== normalizedClientId;

//   this.currentClientId = normalizedClientId;

//   // console.log(
//   //   '[Notifications] currentClientId:',
//   //   this.currentClientId
//   // );

//   if (
//     this.stompClient &&
//     this.stompClient.connected
//   ) {
//     // if (clientChanged) {
//     //   this.subscribeForCurrentUser();
//     // }

//     return;
//   }

//   this.connect();
//   }

// connect(): void {

//   console.log(
//     '[Notifications] connect()',
//     {
//       currentClientId: this.currentClientId,
//       currentUserId: this.currentUserId,
//       websocketUrl: environment.websocketUrl
//     }
//   );

//   if (!this.currentClientId) {
//     console.warn(
//       '[Notifications] connection skipped: clientId missing'
//     );
//     return;
//   }

//   if (
//     this.stompClient &&
//     (
//       this.stompClient.active ||
//       this.stompClient.connected
//     )
//   ) {
//     console.log(
//       '[Notifications] STOMP already active/connected'
//     );
//     return;
//   }

//   try {

//     this.stompClient =
//       new Client({

//         webSocketFactory: () => {
//           console.log(
//             '[Notifications] creating SockJS connection'
//           );

//           return this.createSockJsConnection();
//         },

//         reconnectDelay: 5000,
//         heartbeatIncoming: 10000,
//         heartbeatOutgoing: 10000,

//         debug: (message: string) => {
//           console.log(
//             '[Notification STOMP DEBUG]',
//             message
//           );
//         }
//       });

//     this.stompClient.beforeConnect =
//       async () => {

//         console.log(
//           '[Notifications] beforeConnect()'
//         );

//         const token =
//           await this.authService.getAccessToken();

//         if (!token) {
//           console.error(
//             '[Notifications] access token missing'
//           );

//           throw new Error(
//             'Cannot connect notification WebSocket without an access token.'
//           );
//         }

//         this.currentUserId =
//           this.getUserIdFromToken(token);

//         console.log(
//           '[Notifications] JWT userId:',
//           this.currentUserId
//         );

//         if (!this.currentUserId) {
//           throw new Error(
//             'userId is missing from access token.'
//           );
//         }

//         if (this.stompClient) {

//           this.stompClient.connectHeaders = {
//             Authorization: 'Bearer ' + token,
//             userId: this.currentUserId
//           };

//           console.log(
//             '[Notifications] STOMP CONNECT headers configured',
//             {
//               userId: this.currentUserId,
//               authorizationPresent: true
//             }
//           );
//         }
//       };

//     this.stompClient.onConnect =
//       frame => {

//         console.log(
//           '[Notifications] ===== STOMP CONNECTED ====='
//         );

//         console.log(
//           '[Notifications] connect frame headers:',
//           frame.headers
//         );

//         console.log(
//           '[Notifications] expected userId:',
//           this.currentUserId
//         );

//         console.log(
//           '[Notifications] server principal:',
//           frame.headers['user-name']
//         );

//         this.connectedSubject.next(true);

//         /*
//          * Subscribe to future live notifications.
//          */
//         this.subscribeForCurrentUser();

//         /*
//          * Load existing notifications after refresh/login.
//          */
//         // this.loadNotificationHistory();
//       };

//     this.stompClient.onDisconnect =
//       frame => {

//         console.warn(
//           '[Notifications] STOMP disconnected:',
//           frame.headers
//         );

//         this.connectedSubject.next(false);
//         this.clearSubscriptionState();
//       };

//     this.stompClient.onStompError =
//       frame => {

//         console.error(
//           '[Notifications] ===== STOMP ERROR ====='
//         );

//         console.error(
//           '[Notifications] headers:',
//           frame.headers
//         );

//         console.error(
//           '[Notifications] body:',
//           frame.body
//         );

//         const errorMessage =
//           frame.headers?.message ||
//           'Notification STOMP error';

//         this.connectedSubject.next(false);
//         this.errorSubject.next(errorMessage);
//       };

//     this.stompClient.onWebSocketError =
//       (event: Event) => {

//         console.error(
//           '[Notifications] WebSocket error:',
//           event
//         );

//         this.connectedSubject.next(false);

//         this.errorSubject.next(
//           'Notification WebSocket connection failed.'
//         );
//       };

//     this.stompClient.onWebSocketClose =
//       (event: CloseEvent) => {

//         console.error(
//           '[Notifications] WebSocket closed',
//           {
//             code: event.code,
//             reason: event.reason,
//             wasClean: event.wasClean
//           }
//         );

//         this.connectedSubject.next(false);
//         this.clearSubscriptionState();
//       };

//     this.stompClient.onUnhandledMessage =
//       (message: IMessage) => {

//         console.warn(
//           '[Notifications] UNHANDLED STOMP MESSAGE',
//           {
//             headers: message.headers,
//             body: message.body
//           }
//         );
//       };

//     console.log(
//       '[Notifications] activating STOMP client'
//     );

//     this.stompClient.activate();

//   } catch (error) {

//     console.error(
//       '[Notifications] activate failed:',
//       error
//     );

//     this.connectedSubject.next(false);

//     this.errorSubject.next(
//       'Unable to initialize notification connection.'
//     );
//   }
// }
  connect(): void {

    console.log(
      '[Notifications] connect()',
      {
        websocketUrl: environment.websocketUrl
      }
    );

    if (
      this.stompClient &&
      (
        this.stompClient.active ||
        this.stompClient.connected
      )
    ) {
      console.log(
        '[Notifications] STOMP already active/connected'
      );
      return;
    }

    try {

      this.stompClient =
        new Client({

          /*
          * Native persistent WebSocket.
          *
          * Example:
          * wss://registration.rcm-cec.com/registration/ws
          */
          brokerURL: environment.websocketUrl,

          /*
          * Reconnect ONLY after connection is lost.
          * This does NOT reconnect every 5 seconds
          * while the socket is healthy.
          */
          reconnectDelay: 5000,

          /*
          * STOMP heartbeat over the existing
          * WebSocket connection.
          */
          heartbeatIncoming: 10000,
          heartbeatOutgoing: 10000,

          debug: (message: string) => {
            console.log(
              '[Notification STOMP DEBUG]',
              message
            );
          }
        });

      /*
      * Executed before the first connection
      * and before every reconnect.
      *
      * This is important because the access token
      * may have been refreshed while the socket
      * was connected.
      */
      this.stompClient.beforeConnect =
        async () => {

          console.log(
            '[Notifications] beforeConnect()'
          );

          const token =
            await this.authService.getAccessToken();

          if (!token) {

            console.error(
              '[Notifications] access token missing'
            );

            throw new Error(
              'Cannot connect notification WebSocket without an access token.'
            );
          }

          /*
          * Backend reads this from the STOMP
          * CONNECT frame.
          *
          * Browser WebSocket handshake itself
          * cannot carry our custom Authorization
          * header.
          */
          if (this.stompClient) {

            this.stompClient.connectHeaders = {
              Authorization: `Bearer ${token}`
            };

            console.log(
              '[Notifications] STOMP Authorization configured'
            );
          }
        };

      this.stompClient.onConnect =
        frame => {

          console.log(
            '[Notifications] ===== NATIVE STOMP CONNECTED ====='
          );

          console.log(
            '[Notifications] connect frame headers:',
            frame.headers
          );

          this.connectedSubject.next(true);

          /*
          * Subscribe every time the connection is
          * established/re-established.
          */
          this.subscribeForCurrentUser();
        };

      this.stompClient.onDisconnect =
        frame => {

          console.warn(
            '[Notifications] STOMP disconnected:',
            frame.headers
          );

          this.connectedSubject.next(false);

          this.clearSubscriptionState();
        };

      this.stompClient.onStompError =
        frame => {

          console.error(
            '[Notifications] ===== STOMP ERROR ====='
          );

          console.error(
            '[Notifications] headers:',
            frame.headers
          );

          console.error(
            '[Notifications] body:',
            frame.body
          );

          const errorMessage =
            frame.headers?.message ||
            'Notification STOMP error';

          this.connectedSubject.next(false);

          this.errorSubject.next(
            errorMessage
          );
        };

      this.stompClient.onWebSocketError =
        (event: Event) => {

          console.error(
            '[Notifications] WebSocket error:',
            event
          );

          this.connectedSubject.next(false);

          this.errorSubject.next(
            'Notification WebSocket connection failed.'
          );
        };

      this.stompClient.onWebSocketClose =
        (event: CloseEvent) => {

          console.warn(
            '[Notifications] WebSocket closed',
            {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean
            }
          );

          this.connectedSubject.next(false);

          this.clearSubscriptionState();

          /*
          * Do NOT call connect() here.
          *
          * @stomp/stompjs handles reconnection
          * automatically using reconnectDelay.
          */
        };

      this.stompClient.onUnhandledMessage =
        (message: IMessage) => {

          console.warn(
            '[Notifications] UNHANDLED STOMP MESSAGE',
            {
              headers: message.headers,
              body: message.body
            }
          );
        };

      console.log(
        '[Notifications] activating native STOMP WebSocket'
      );

      this.stompClient.activate();

    } catch (error) {

      console.error(
        '[Notifications] WebSocket initialization failed:',
        error
      );

      this.connectedSubject.next(false);

      this.errorSubject.next(
        'Unable to initialize notification connection.'
      );
    }
  }
  // private subscribeForCurrentUser(): void {

  //   console.log(
  //     '[Notifications] subscribeForCurrentUser()'
  //   );

  //   if (!this.stompClient) {
  //     console.error(
  //       '[Notifications] subscription failed: stompClient is null'
  //     );
  //     return;
  //   }

  //   if (!this.stompClient.connected) {
  //     console.error(
  //       '[Notifications] subscription failed: STOMP not connected'
  //     );
  //     return;
  //   }

  //   if (!this.currentUserId) {
  //     console.error(
  //       '[Notifications] subscription failed: currentUserId missing'
  //     );
  //     return;
  //   }

  //   const destination =
  //     this.getSubscriptionDestination();

  //   console.log(
  //     '[Notifications] subscription information:',
  //     {
  //       userId: this.currentUserId,
  //       destination,
  //       connected: this.stompClient.connected
  //     }
  //   );

  //   if (
  //     this.topicSubscription &&
  //     this.subscribedDestination === destination
  //   ) {

  //     console.log(
  //       '[Notifications] already subscribed:',
  //       destination
  //     );

  //     return;
  //   }

  //   this.unsubscribeFromCurrentTopic();

  //   try {

  //     this.topicSubscription =
  //       this.stompClient.subscribe(
  //         destination,
  //         (message: IMessage) => {

  //           console.log(
  //             '[Notifications] ===== RAW MESSAGE RECEIVED ====='
  //           );

  //           console.log(
  //             '[Notifications] headers:',
  //             message.headers
  //           );

  //           console.log(
  //             '[Notifications] body:',
  //             message.body
  //           );

  //           this.ngZone.run(() => {

  //             console.log(
  //               '[Notifications] running message processing inside NgZone'
  //             );

  //             this.handleNotificationMessage(
  //               message
  //             );
  //           });
  //         }
  //       );

  //     this.subscribedDestination =
  //       destination;

  //     console.log(
  //       '[Notifications] subscription SUCCESS',
  //       {
  //         destination,
  //         subscriptionId:
  //           this.topicSubscription.id
  //       }
  //     );

  //   } catch (error) {

  //     console.error(
  //       '[Notifications] subscription FAILED:',
  //       error
  //     );
  //   }
  // }
private subscribeForCurrentUser(): void {

  if (
    !this.stompClient ||
    !this.stompClient.connected
  ) {
    return;
  }

  const destination =
    '/user/queue/notifications';

  if (
    this.topicSubscription &&
    this.subscribedDestination === destination
  ) {
    return;
  }

  this.unsubscribeFromCurrentTopic();

  try {

    this.topicSubscription =
      this.stompClient.subscribe(
        destination,
        (message: IMessage) => {

          this.ngZone.run(() => {

            this.handleNotificationMessage(
              message
            );

          });
        }
      );

    this.subscribedDestination =
      destination;

    console.log(
      '[Notifications] subscribed:',
      destination
    );

  } catch (error) {

    console.error(
      '[Notifications] subscription failed:',
      error
    );
  }
}
  // private getUserIdFromToken(token: string): string | null {
  //   try {
  //     const payloadPart = token.split('.')[1];

  //     const normalizedPayload = payloadPart
  //       .replace(/-/g, '+')
  //       .replace(/_/g, '/');

  //     const payload = JSON.parse(
  //       decodeURIComponent(
  //         atob(normalizedPayload)
  //           .split('')
  //           .map((character: string) => {
  //             return (
  //               '%' +
  //               ('00' + character.charCodeAt(0).toString(16))
  //                 .slice(-2)
  //             );
  //           })
  //           .join('')
  //       )
  //     );

  //     return payload.userId || null;
  //   } catch (error) {
  //     // console.error(
  //     //   '[Notifications] unable to decode userId:',
  //     //   error
  //     // );

  //     return null;
  //   }
  // }

  // private createSockJsConnection(): any {
  //   try {
  //     const SockJsConstructor: any =
  //       (SockJSClient as any).default ||
  //       SockJSClient;

  //     if (
  //       typeof SockJsConstructor !== 'function'
  //     ) {
  //       throw new Error(
  //         'SockJS constructor is unavailable.'
  //       );
  //     }

  //     // console.log(
  //     //   '[Notifications] creating SockJS connection:',
  //     //   environment.websocketUrl
  //     // );

  //     return new SockJsConstructor(
  //       environment.websocketUrl
  //     );
  //   } catch (error) {
  //     // console.error(
  //     //   '[Notifications] SockJS creation failed:',
  //     //   error
  //     // );

  //     this.connectedSubject.next(false);
  //     this.errorSubject.next(
  //       'Unable to create notification connection.'
  //     );

  //     throw error;
  //   }
  // }

  /*
   * Keep destination construction isolated.
   * Later this can simply return:
   *
   * return '/user/queue/notifications';
   */
  // private getSubscriptionDestination(
  //   clientId: number
  // ): string {
  //   return (
  //     '/topic/client/' +
  //     clientId +
  //     '/notifications'
  //   );
  // }
//   private getSubscriptionDestination(): string {
//   if (!this.currentUserId) {
//     throw new Error(
//       'Cannot create notification destination without userId.'
//     );
//   }

//   return (
//     '/' +
//     this.currentUserId +
//     '/queue/notifications'
//   );
// }
private getSubscriptionDestination(): string {
  return '/user/queue/notifications';
}

private handleNotificationMessage(
  message: IMessage
): void {

  console.log(
    '[Notifications] handleNotificationMessage()'
  );

  try {

    const response: any =
      JSON.parse(message.body);

    console.log(
      '[Notifications] parsed response:',
      response
    );

    const notification:
      NotificationResponse = {

      ...response,

      read:
        typeof response.read === 'boolean'
          ? response.read
          : false,

      readAt:
        response.readAt || null
    };

    console.log(
      '[Notifications] normalized notification:',
      notification
    );

    const valid =
      this.isValidNotification(
        notification
      );

    console.log(
      '[Notifications] notification valid:',
      valid
    );

    if (!valid) {

      console.error(
        '[Notifications] INVALID notification:',
        notification
      );

      this.errorSubject.next(
        'An invalid notification was received.'
      );

      return;
    }

    this.addNotification(
      notification
    );

  } catch (error) {

    console.error(
      '[Notifications] unable to parse message',
      {
        error,
        body: message.body
      }
    );

    this.errorSubject.next(
      'Unable to parse notification message.'
    );
  }
}

  private isValidNotification(
  notification: NotificationResponse
): boolean {

  if (!notification) {
    console.error(
      '[Notifications] validation: object missing'
    );
    return false;
  }

  if (typeof notification.id !== 'number') {
    console.error(
      '[Notifications] validation: invalid id',
      notification.id
    );
    return false;
  }

  if (
    typeof notification.clientId !== 'number'
  ) {
    console.error(
      '[Notifications] validation: invalid clientId',
      notification.clientId
    );
    return false;
  }

  if (
    typeof notification.notificationType
      !== 'string'
  ) {
    console.error(
      '[Notifications] validation: invalid type',
      notification.notificationType
    );
    return false;
  }

  if (
    typeof notification.title !== 'string'
  ) {
    console.error(
      '[Notifications] validation: invalid title',
      notification.title
    );
    return false;
  }

  if (
    typeof notification.message !== 'string'
  ) {
    console.error(
      '[Notifications] validation: invalid message',
      notification.message
    );
    return false;
  }

  if (
    typeof notification.read !== 'boolean'
  ) {
    console.error(
      '[Notifications] validation: invalid read',
      notification.read
    );
    return false;
  }

  if (
    typeof notification.eventTime !== 'string'
  ) {
    console.error(
      '[Notifications] validation: invalid eventTime',
      notification.eventTime
    );
    return false;
  }

  return true;
}

 private addNotification(
  notification: NotificationResponse
): void {

  console.log(
    '[Notifications] addNotification()',
    notification
  );

  const current =
    this.notificationsSubject.value;

  console.log(
    '[Notifications] current notifications:',
    current
  );

  const alreadyExists =
    current.some(
      (item: NotificationResponse) =>
        item.id === notification.id
    );

  if (alreadyExists) {

    console.warn(
      '[Notifications] duplicate ignored:',
      notification.id
    );

    return;
  }

  const updated = [
    notification,
    ...current
  ];

  console.log(
    '[Notifications] updated notifications:',
    updated
  );

  this.notificationsSubject.next(
    updated
  );

  this.updateUnreadCount(
    updated
  );

  console.log(
    '[Notifications] notification pushed to BehaviorSubject'
  );
  
  this.liveNotificationSubject.next(
    notification
  );
}

 appendNotificationHistory(
  history: NotificationResponse[]
): void {
  if (!Array.isArray(history)) {
    return;
  }

  const normalizedHistory =
    history.map(
      (notification: NotificationResponse) => ({
        ...notification,
        read:
          typeof notification.read === 'boolean'
            ? notification.read
            : false,
        readAt:
          notification.readAt || null
      })
    );

  const notificationMap =
    new Map<number, NotificationResponse>();

  /*
   * Add current notifications first so a notification
   * received live through WebSocket is preserved.
   */
  this.notificationsSubject.value.forEach(
    (notification: NotificationResponse) => {
      notificationMap.set(
        notification.id,
        notification
      );
    }
  );

  normalizedHistory.forEach(
    (notification: NotificationResponse) => {
      if (!notificationMap.has(notification.id)) {
        notificationMap.set(
          notification.id,
          notification
        );
      }
    }
  );

  const mergedNotifications =
    Array.from(notificationMap.values())
      .sort(
        (
          first: NotificationResponse,
          second: NotificationResponse
        ) =>
          this.getNotificationTimestamp(second) -
          this.getNotificationTimestamp(first)
      );

  this.notificationsSubject.next(
    mergedNotifications
  );

  this.updateUnreadCount(
    mergedNotifications
  );
 }

  private getNotificationTimestamp(
  notification: NotificationResponse
): number {
  if (!notification.eventTime) {
    return 0;
  }

  const timestamp =
    new Date(notification.eventTime).getTime();

  return Number.isNaN(timestamp)
    ? 0
    : timestamp;
  }

  markAsRead(notificationId: number): void {
    const updated =
      this.notificationsSubject.value.map(
        (notification: NotificationResponse) => {
          if (notification.id === notificationId) {
            return {
              ...notification,
              read: true
            };
          }

          return notification;
        }
      );

    this.notificationsSubject.next(updated);
    this.updateUnreadCount(updated);
  }

  markAllAsRead(): void {
    const updated =
      this.notificationsSubject.value.map(
        (notification: NotificationResponse) => {
          return {
            ...notification,
            read: true
          };
        }
      );

    this.notificationsSubject.next(updated);
    this.updateUnreadCount(updated);
  }

 private updateUnreadCount(
  notifications: NotificationResponse[]
): void {

  const unreadCount =
    notifications.filter(
      (notification: NotificationResponse) =>
        !notification.read
    ).length;

  console.log(
    '[Notifications] unread count:',
    unreadCount
  );

  this.unreadCountSubject.next(
    unreadCount
  );
}

  private unsubscribeFromCurrentTopic(): void {
    if (this.topicSubscription) {
      try {
        this.topicSubscription.unsubscribe();
      } catch (error) {
        console.error(
          'Unable to unsubscribe notification topic:',
          error
        );
      }
    }

    this.clearSubscriptionState();
  }

  private clearSubscriptionState(): void {
    this.topicSubscription = null;
    this.subscribedDestination = null;
  }

  disconnect(): void {
    this.unsubscribeFromCurrentTopic();
    this.connectedSubject.next(false);

    if (
      this.stompClient &&
      this.stompClient.active
    ) {
      this.stompClient.deactivate();
    }

    this.stompClient = null;
    this.currentClientId = null;
    this.currentUserId = null;

    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }

  destroy(): void {
    this.disconnect();

    if (this.clientIdSubscription) {
      this.clientIdSubscription.unsubscribe();
      this.clientIdSubscription = null;
    }

    if (this.loginSubscription) {
      this.loginSubscription.unsubscribe();
      this.loginSubscription = null;
    }

    this.initialized = false;
  }
}
